import { supabase } from '../../config/supabase';
import { MarkerAction } from '../../types/marker';
import logger from '../../utils/logger';
import { toISOStringIfDate } from '../../utils/formatters';

/**
 * Convertit une action au format snake_case pour Supabase
 */
function convertActionToSnakeCase(action: Omit<MarkerAction, 'id'> & { agency_id?: string | null }) {
  return {
    marker_id: action.markerId,
    date: toISOStringIfDate(action.date),
    type: action.type,
    description: action.description,
    status: action.status || 'pending',
    reminder_date: toISOStringIfDate(action.reminderDate),
    is_system: action.is_system ?? false,
    is_temporary: action.isTemporary ?? false,
    next_follow_up: toISOStringIfDate(action.nextFollowUp),
    details: action.details || null,
    user_id: action.user_id || null,
    agency_id: action.agency_id || null
  };
}

/**
 * Convertit une action du format snake_case de Supabase au format de l'application
 */
function convertActionFromSnakeCase(action: any): MarkerAction {
  return {
    id: action.id,
    markerId: action.marker_id,
    date: new Date(action.date),
    type: action.type,
    description: action.description,
    status: action.status,
    reminderDate: action.reminder_date ? new Date(action.reminder_date) : undefined,
    nextFollowUp: action.next_follow_up ? new Date(action.next_follow_up) : undefined,
    details: action.details,
    is_system: action.is_system,
    isTemporary: action.is_temporary,
    createdAt: new Date(action.created_at),
    updatedAt: new Date(action.updated_at),
    user_id: action.user_id
  };
}

export const actionsService = {
  async getAll(): Promise<MarkerAction[]> {
    try {
      const { data, error } = await supabase
        .from('actions')
        .select('*')
        .order('date', { ascending: false });

      if (error) {
        logger.error('Erreur lors de la récupération des actions:', error);
        throw error;
      }

      return (data || []).map(convertActionFromSnakeCase);
    } catch (error: any) {
      logger.error('Exception lors de la récupération des actions:', error);
      throw error;
    }
  },

  async getByMarkerId(markerId: string): Promise<MarkerAction[]> {
    try {
      const { data, error } = await supabase
        .from('actions')
        .select('*')
        .eq('marker_id', markerId)
        .order('date', { ascending: false });

      if (error) {
        logger.error('Erreur lors de la récupération des actions du marqueur:', error);
        throw error;
      }

      return (data || []).map(convertActionFromSnakeCase);
    } catch (error: any) {
      logger.error('Exception lors de la récupération des actions du marqueur:', error);
      throw error;
    }
  },

  async getById(id: string): Promise<MarkerAction | null> {
    try {
      const { data, error } = await supabase
        .from('actions')
        .select('*')
        .eq('id', id)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          return null;
        }
        logger.error('Erreur lors de la récupération de l\'action:', error);
        throw error;
      }

      if (!data) return null;
      return convertActionFromSnakeCase(data);
    } catch (error: any) {
      logger.error('Exception lors de la récupération de l\'action:', error);
      throw error;
    }
  },

  async create(action: Omit<MarkerAction, 'id'> & { agency_id?: string | null }): Promise<MarkerAction> {
    try {
      logger.debug('Création d\'action avec les données:', action);
      
      const actionData = convertActionToSnakeCase(action);
      logger.debug('Données converties pour Supabase:', actionData);
      
      const { data, error } = await supabase
        .from('actions')
        .insert(actionData)
        .select()
        .single();
      
      if (error) {
        logger.error('Erreur lors de la création de l\'action:', error);
        throw error;
      }

      if (!data) {
        throw new Error('Aucune donnée retournée après la création de l\'action');
      }

      const convertedAction = convertActionFromSnakeCase(data);
      logger.info('Action créée avec succès:', { id: convertedAction.id, type: convertedAction.type });
      
      return convertedAction;
    } catch (error) {
      logger.error('Erreur dans actionsService.create:', error);
      throw error;
    }
  },

  async update(id: string, action: Partial<Omit<MarkerAction, 'id'>>): Promise<MarkerAction> {
    try {
      const existingAction = await this.getById(id);
      if (!existingAction) {
        throw new Error(`L'action avec l'ID ${id} n'existe pas`);
      }

      const updateData: Record<string, any> = {};
      
      if (action.markerId !== undefined) updateData.marker_id = action.markerId;
      if (action.date !== undefined) updateData.date = toISOStringIfDate(action.date);
      if (action.type !== undefined) updateData.type = action.type;
      if (action.description !== undefined) updateData.description = action.description;
      if (action.status !== undefined) updateData.status = action.status;
      if (action.reminderDate !== undefined) updateData.reminder_date = toISOStringIfDate(action.reminderDate);
      if (action.nextFollowUp !== undefined) updateData.next_follow_up = toISOStringIfDate(action.nextFollowUp);
      if (action.details !== undefined) updateData.details = action.details;
      if (action.is_system !== undefined) updateData.is_system = action.is_system;
      if (action.isTemporary !== undefined) updateData.is_temporary = action.isTemporary;
      if (action.user_id !== undefined) updateData.user_id = action.user_id;

      const { data, error } = await supabase
        .from('actions')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        logger.error('Erreur lors de la mise à jour de l\'action:', error);
        throw error;
      }

      return convertActionFromSnakeCase(data);
    } catch (error: any) {
      logger.error('Exception lors de la mise à jour de l\'action:', error);
      throw error;
    }
  },

  async delete(id: string): Promise<void> {
    try {
      const existingAction = await this.getById(id);
      if (!existingAction) {
        throw new Error(`L'action avec l'ID ${id} n'existe pas`);
      }

      const { error } = await supabase
        .from('actions')
        .delete()
        .eq('id', id);

      if (error) {
        logger.error('Erreur lors de la suppression de l\'action:', error);
        throw error;
      }
    } catch (error: any) {
      logger.error('Exception lors de la suppression de l\'action:', error);
      throw error;
    }
  },

  async confirmTemporaryActions(markerId: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('actions')
        .update({ is_temporary: false })
        .eq('marker_id', markerId)
        .eq('is_temporary', true);

      if (error) {
        logger.error('Erreur lors de la confirmation des actions temporaires:', error);
        throw error;
      }
    } catch (error: any) {
      logger.error('Exception lors de la confirmation des actions temporaires:', error);
      throw error;
    }
  },

  async getTemporaryActionsByMarkerId(markerId: string): Promise<MarkerAction[]> {
    try {
      const { data, error } = await supabase
        .from('actions')
        .select('*')
        .eq('marker_id', markerId)
        .eq('is_temporary', true)
        .order('date', { ascending: false });

      if (error) {
        logger.error('Erreur lors de la récupération des actions temporaires:', error);
        throw error;
      }

      return (data || []).map(convertActionFromSnakeCase);
    } catch (error: any) {
      logger.error('Exception lors de la récupération des actions temporaires:', error);
      throw error;
    }
  },

  async createTemporaryAction(action: Omit<MarkerAction, 'id'>): Promise<MarkerAction> {
    try {
      const temporaryAction = {
        ...action,
        isTemporary: true
      };

      return await this.create(temporaryAction);
    } catch (error: any) {
      logger.error('Exception lors de la création de l\'action temporaire:', error);
      throw error;
    }
  },

  async getCustomActionTypes(): Promise<string[]> {
    try {
      const { data, error } = await supabase
        .from('actions')
        .select('type')
        .eq('is_system', false);

      if (error) {
        logger.error('Erreur lors de la récupération des types d\'actions personnalisés:', error);
        throw error;
      }

      const uniqueTypes = Array.from(new Set((data || []).map(item => item.type)));
      const systemTypes = ['call', 'email', 'visit', 'follow_up', 'reminder'];
      return uniqueTypes.filter(type => !systemTypes.includes(type));
    } catch (error: any) {
      logger.error('Exception lors de la récupération des types d\'actions personnalisés:', error);
      throw error;
    }
  }
};

